import React, { FC, useMemo, useState } from "react"
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Stack,
  IconButton,
  Collapse,
  Link,
} from "@mui/material"
import { ExpandMore, ExpandLess } from "@mui/icons-material"
import { DataGrid } from "@/core/components/dataGrid/dataGrid"
import { GridColDef } from "@mui/x-data-grid-pro"
import { useEventLogsByChannelQuery } from "../../services/channels/channelsApi"
import { IEventLog, EventStatus } from "../../interface/interface"
import { format } from "date-fns"
import { ru } from "date-fns/locale"

interface IChannelEventLogsSectionProps {
  channelId: string
}

export const ChannelEventLogsSection: FC<IChannelEventLogsSectionProps> = ({
  channelId,
}) => {
  const [eventLogsOpen, setEventLogsOpen] = useState(false)
  const {
    currentData: logs,
    isLoading,
    isError,
  } = useEventLogsByChannelQuery(channelId)

  const columns: GridColDef<IEventLog>[] = useMemo(
    () => [
      {
        field: "event_type",
        headerName: "Тип события",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "message",
        headerName: "Сообщение",
        flex: 2,
        minWidth: 300,
      },
      {
        field: "post_id",
        headerName: "ID поста",
        flex: 0.6,
        minWidth: 100,
        renderCell: (params) => {
          return params.value ?? "—"
        },
      },
      {
        field: "message_id",
        headerName: "Ссылка на пост",
        flex: 1,
        minWidth: 120,
        renderCell: (params) => {
          const messageId = params.value
          const row = params.row as IEventLog

          if (!messageId || !row.channel_id) return "—"

          const telegramLink = `tg://openmessage?chat_id=${row.channel_id}&message_id=${messageId}`

          return (
            <Link
              href={telegramLink}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ textDecoration: "none" }}
            >
              Открыть
            </Link>
          )
        },
      },
      {
        field: "status",
        headerName: "Статус",
        flex: 0.8,
        minWidth: 120,
        renderCell: (params) => {
          const status = params.value as EventStatus | null
          if (!status) return "—"

          const colors = {
            success: "success.main",
            failed: "error.main",
            warning: "warning.main",
          }

          const labels = {
            success: "Успешно",
            failed: "Ошибка",
            warning: "Предупреждение",
          }

          return <Box sx={{ color: colors[status] }}>{labels[status]}</Box>
        },
      },
      {
        field: "created_at",
        headerName: "Дата",
        flex: 1,
        minWidth: 180,
        renderCell: (params) => {
          try {
            return format(new Date(params.value), "dd.MM.yyyy HH:mm:ss", {
              locale: ru,
            })
          } catch {
            return params.value
          }
        },
      },
    ],
    [],
  )

  return (
    <Paper sx={{ p: 3, m: 2 }}>
      <Stack spacing={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Логи событий</Typography>
          <IconButton
            onClick={() => setEventLogsOpen(!eventLogsOpen)}
            aria-label="показать логи событий"
          >
            {eventLogsOpen ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        <Collapse in={eventLogsOpen}>
          <Box mt={2}>
            {isLoading && <CircularProgress size={24} />}

            {isError && !isLoading && (
              <Alert severity="error">Не удалось загрузить логи событий</Alert>
            )}

            {!logs?.length && !isLoading && !isError && (
              <Typography variant="body2" color="text.secondary">
                Нет событий для отображения
              </Typography>
            )}

            {logs && logs.length > 0 && !isLoading && !isError && (
              <Box sx={{ height: "auto", width: "100%" }}>
                <DataGrid
                  rows={logs}
                  columns={columns}
                  autoHeight
                  density="compact"
                  disableRowSelectionOnClick
                  pagination
                  pageSizeOptions={[5, 10, 25, 50]}
                  initialState={{
                    pagination: {
                      paginationModel: { pageSize: 10, page: 0 },
                    },
                  }}
                />
              </Box>
            )}
          </Box>
        </Collapse>
      </Stack>
    </Paper>
  )
}
